import { useState } from 'react';
import { motion, AnimatePresence } from 'motion/react';
import { ArrowRight, ArrowLeft, ShieldCheck } from 'lucide-react';
import { Link } from 'react-router-dom';
import { AFFILIATE_LINKS } from '../constants/affiliates';
import { auth } from '../lib/firebase';
import { dbService } from '../services/dbService';

const questions = [
    { id: "Q1", section: "1", category: "THE RATIONALIZATION PIVOT", text: "When a promising romantic connection stalls or fades, your immediate cognitive pivot is to assume your competence, intellect, or lifestyle was simply 'too much' or 'too intimidating' for them to handle." },
    { id: "Q2", section: "2", category: "OPERATIONALIZED INTIMACY", text: "You manage the early stages of dating with the same rigorous, risk-mitigating precision you use in your professional life, unconsciously treating vulnerability as an operational hazard to be managed rather than an experience to be felt." },
    { id: "Q3", section: "3", category: "THE ASYMMETRICAL SAFE HAVEN", text: "You feel significantly more comfortable acting as the stable, resource-giving partner who solves problems than you do exposing a raw, non-negotiable emotional need that you cannot resolve on your own." },
    { id: "Q4", section: "4", category: "PREEMPTIVE SELF-SUFFICIENCY", text: "You experience a subtle sense of safety in knowing you do not 'need' anyone, using your financial and logistical self-reliance as a preemptive boundary to ensure you can never be caught off guard or abandoned." },
    { id: "Q5", section: "5", category: "THE ELEGANT FORTRESS", text: "You frame your romantic solitude exclusively as a necessary consequence of having 'high standards,' rarely examining whether those standards have been calibrated to a level that functions as an elegant, socially acceptable wall." },
];

export default function IntimidationDelusionAudit() {
    const [step, setStep] = useState<'INTRO' | 'ASSESSMENT' | 'RESULTS'>('INTRO');
    const [currentQuestionIndex, setCurrentQuestionIndex] = useState(0);
    const [answers, setAnswers] = useState<Record<string, number>>({});
    const [isCompiling, setIsCompiling] = useState(false);

    const handleStart = () => setStep('ASSESSMENT');

    const handleAnswer = (value: number) => {
        const question = questions[currentQuestionIndex];
        setAnswers(prev => ({ ...prev, [question.id]: value }));
    };

    const handlePrev = () => {
        if (currentQuestionIndex > 0) {
            setCurrentQuestionIndex(prev => prev - 1);
            window.scrollTo({ top: 0, behavior: 'smooth' });
        }
    };

    const calculateResults = () => {
        let total = 0;
        
        questions.forEach((q) => {
            const val = answers[q.id] || 3; // Default 3 for neutral
            total += val;
        });

        let profileType = "secure";
        let summaryText = "";
        let gapType = "Secure / Transparent Autonomy";

        if (total >= 20) {
            profileType = "extreme";
            gapType = "Extreme Impenetrability";
            summaryText = "Your professional success has become an elegant fortress. Extreme emotional shielding disguised as being intimidating.";
        } else if (total >= 13) {
            profileType = "emerging";
            gapType = "Emerging Relational Vigilance";
            summaryText = "You are beginning to use your professional competence as an emotional buffer.";
        } else {
            profileType = "secure";
            summaryText = "You approach intimacy without needing your success to act as an emotional shield.";
        }

        return { total, profileType, gapType, summaryText };
    };

    const handleNext = async () => {
        if (currentQuestionIndex < questions.length - 1) {
            setCurrentQuestionIndex(prev => prev + 1);
            window.scrollTo({ top: 0, behavior: 'smooth' });
        } else {
            setStep('RESULTS');
            setIsCompiling(true);

            // Save to DB
            try {
                const user = auth.currentUser;
                if (!user) throw new Error("User not found");

                const { gapType, summaryText } = calculateResults();

                await dbService.saveAuditResult(user.uid, {
                    auditType: 'intimidation_delusion',
                    title: 'The Intimidation Delusion Audit',
                    diagnostic: gapType,
                    summary: summaryText
                });
            } catch (err) {
                console.error("Failed to persist audit results:", err);
            }

            setTimeout(() => setIsCompiling(false), 3000);
            window.scrollTo({ top: 0, behavior: 'smooth' });
        }
    };

    return (
        <div className="min-h-screen flex flex-col font-sans text-theme-text bg-theme-bg selection:bg-gold/30">
            <main className="flex-grow flex flex-col items-center justify-start w-full pt-20 pb-24">
                <AnimatePresence mode="wait">
                    {step === 'INTRO' && (
                        <motion.div
                            key="intro"
                            initial={{ opacity: 0, y: 20 }}
                            animate={{ opacity: 1, y: 0 }}
                            exit={{ opacity: 0, y: -20 }}
                            className="flex flex-col items-center text-center max-w-2xl mx-auto px-6 py-12"
                        >
                            <h1 className="font-serif text-4xl md:text-5xl uppercase tracking-[0.15em] text-theme-text mb-8 leading-tight">
                                The Intimidation Delusion Audit
                            </h1>
                            <div className="w-16 h-[1px] bg-gold mb-8"></div>
                            <p className="text-theme-text/80 text-lg leading-relaxed mb-6 font-light uppercase tracking-widest text-[10px]">
                                The Defensive Success Matrix
                            </p>
                            <p className="text-theme-text/60 text-sm leading-relaxed mb-12 max-w-xl mx-auto italic">
                                This diagnostic tool evaluates whether professional success, intellect, or independence is being unconsciously deployed as an emotional shield. Read each statement carefully and respond with your automatic patterns, rather than your intellectualized ideals.
                            </p>

                            <button
                                onClick={handleStart}
                                className="group relative px-8 py-4 bg-transparent border border-gold text-theme-text uppercase tracking-widest text-sm font-medium overflow-hidden transition-all hover:text-white cursor-pointer"
                            >
                                <div className="absolute inset-0 bg-gold translate-y-full group-hover:translate-y-0 transition-transform duration-300 ease-out -z-10"></div>
                                <span className="flex items-center gap-3 relative z-10 group-hover:text-theme-bg transition-colors">
                                    Commence Audit <ArrowRight className="w-4 h-4" />
                                </span>
                            </button>
                        </motion.div>
                    )}

                    {step === 'ASSESSMENT' && (
                        <motion.div
                            key="assessment"
                            initial={{ opacity: 0 }}
                            animate={{ opacity: 1 }}
                            exit={{ opacity: 0 }}
                            className="w-full max-w-3xl mx-auto px-6 py-12 flex flex-col items-center pt-24"
                        >
                            <div className="w-full max-w-xl h-[1px] bg-theme-border flex-grow-0 mb-12 relative">
                                <motion.div
                                    className="absolute top-0 left-0 h-full bg-gold shadow-[0_0_8px_rgba(212,175,55,0.5)]"
                                    initial={{ width: 0 }}
                                    animate={{ width: ((currentQuestionIndex) / questions.length) * 100 + '%' }}
                                    transition={{ duration: 0.5 }}
                                />
                            </div>

                            <div className="text-center mb-16 flex flex-col items-center gap-2">
                                <span className="font-serif text-gold tracking-widest uppercase text-base md:text-lg font-semibold">
                                    SECTION {questions[currentQuestionIndex].section}: {questions[currentQuestionIndex].category}
                                </span>
                                <span className="text-xs tracking-widest text-taupe uppercase font-semibold">
                                    {currentQuestionIndex + 1} / {questions.length}
                                </span>
                            </div>

                            <AnimatePresence mode="wait">
                                <motion.div
                                    key={currentQuestionIndex}
                                    initial={{ opacity: 0, x: 20 }}
                                    animate={{ opacity: 1, x: 0 }}
                                    exit={{ opacity: 0, x: -20 }}
                                    transition={{ duration: 0.3 }}
                                    className="w-full flex flex-col items-center"
                                >
                                    <h2 className="text-2xl md:text-3xl font-light text-theme-text text-center leading-relaxed mb-16 min-h-[120px] flex items-center italic">
                                        "{questions[currentQuestionIndex].text}"
                                    </h2>

                                    <div className="w-full max-w-3xl mx-auto">
                                        <div className="flex flex-wrap justify-center sm:flex-nowrap sm:justify-between items-start gap-3 sm:gap-2 w-full">
                                            {[
                                                { val: 1, label: "Strongly Disagree" },
                                                { val: 2, label: "Disagree" },
                                                { val: 3, label: "Neutral" },
                                                { val: 4, label: "Agree" },
                                                { val: 5, label: "Strongly Agree" }
                                            ].map(({ val, label }) => {
                                                const isSelected = answers[questions[currentQuestionIndex].id] === val;
                                                return (
                                                    <div key={val} className="flex flex-col items-center gap-3 w-1/4 sm:w-auto">
                                                        <button
                                                            onClick={() => handleAnswer(val)}
                                                            className={"relative w-10 h-10 sm:w-12 sm:h-12 rounded-full border flex items-center justify-center transition-all duration-300 cursor-pointer shrink-0 " + (isSelected ? "border-gold bg-gold text-theme-bg scale-110 shadow-[0_0_15px_rgba(212,175,55,0.4)]" : "border-theme-border text-theme-text hover:border-gold hover:text-gold hover:scale-110 hover:shadow-[0_0_15px_rgba(212,175,55,0.2)]")}
                                                        >
                                                            <span className="text-sm sm:text-base font-medium">{val}</span>
                                                        </button>
                                                        <span className={"text-[8px] sm:text-[10px] uppercase tracking-widest text-center leading-tight transition-colors mt-2 max-w-[80px] " + (isSelected ? "text-gold font-bold" : "text-taupe")}>
                                                            {label}
                                                        </span>
                                                    </div>
                                                );
                                            })}
                                        </div>

                                        <div className="flex justify-between w-full mt-24 px-4 border-t border-theme-border pt-8">
                                            <button
                                                onClick={handlePrev}
                                                disabled={currentQuestionIndex === 0}
                                                className={"flex items-center gap-2 text-xs uppercase tracking-widest transition-colors font-bold " + (currentQuestionIndex === 0 ? "text-taupe/30 cursor-not-allowed" : "text-taupe hover:text-gold cursor-pointer")}
                                            >
                                                <ArrowLeft className="w-4 h-4" /> Previous
                                            </button>
                                            <button
                                                onClick={handleNext}
                                                disabled={!answers[questions[currentQuestionIndex].id]}
                                                className={"flex items-center gap-2 text-xs uppercase tracking-widest transition-colors font-bold px-6 py-3 border inline-flex " + (!answers[questions[currentQuestionIndex].id] ? "border-transparent text-taupe/30 cursor-not-allowed" : "border-gold text-gold hover:bg-gold hover:text-theme-bg cursor-pointer")}
                                            >
                                                {currentQuestionIndex === questions.length - 1 ? 'Complete Audit' : 'Next Step'} <ArrowRight className="w-4 h-4" />
                                            </button>
                                        </div>
                                    </div>
                                </motion.div>
                            </AnimatePresence>
                        </motion.div>
                    )}

                    {step === 'RESULTS' && (() => {
                        if (isCompiling) {
                            return (
                                <motion.div
                                    key="compiling"
                                    initial={{ opacity: 0 }}
                                    animate={{ opacity: 1 }}
                                    exit={{ opacity: 0 }}
                                    className="w-full max-w-4xl mx-auto px-6 py-24 flex flex-col items-center justify-center min-h-[50vh]"
                                >
                                    <div className="relative w-24 h-24 mb-12">
                                        <div className="absolute inset-0 border-t-2 border-l-2 border-gold rounded-full animate-[spin_2s_linear_infinite]"></div>
                                        <div className="absolute inset-2 border-r-2 border-b-2 border-taupe rounded-full animate-[spin_3s_linear_infinite_reverse]"></div>
                                        <div className="absolute inset-0 flex items-center justify-center">
                                            <ShieldCheck className="w-6 h-6 text-gold animate-pulse" />
                                        </div>
                                    </div>
                                    <h2 className="font-serif text-xl md:text-2xl uppercase tracking-[0.2em] text-gold mb-4 text-center">
                                        Calculating Intimidation Index...
                                    </h2>
                                    <p className="text-theme-text/60 font-light text-sm tracking-widest animate-pulse text-center">
                                        Analyzing defensive frameworks...
                                    </p>
                                </motion.div>
                            );
                        }

                        const profile = calculateResults();
                        const percentage = (profile.total / 25) * 100;
                        
                        const containerVariants = {
                            hidden: { opacity: 0 },
                            visible: {
                                opacity: 1,
                                transition: { staggerChildren: 0.2, delayChildren: 0.1 }
                            }
                        };

                        const itemVariants = {
                            hidden: { opacity: 0, y: 20 },
                            visible: { 
                                opacity: 1, y: 0, 
                                transition: { duration: 0.6, ease: [0.22, 1, 0.36, 1] } as any
                            }
                        };

                        return (
                            <motion.div
                                key="results"
                                variants={containerVariants}
                                initial="hidden"
                                animate="visible"
                                className="w-full max-w-4xl mx-auto px-6 py-12 pb-24"
                            >
                                <motion.div variants={itemVariants} className="text-center mb-16">
                                    <span className="text-xs uppercase tracking-[0.3em] text-gold font-semibold">Clinical Report</span>
                                    <h1 className="font-serif text-3xl md:text-5xl uppercase tracking-widest text-theme-text mt-6 mb-6">
                                        {profile.gapType}
                                    </h1>
                                </motion.div>

                                <motion.div variants={itemVariants} className="w-full h-[1px] bg-gold/30 mb-16" />

                                <motion.div variants={itemVariants} className="grid md:grid-cols-2 gap-12 md:gap-16 mb-16">
                                    <div>
                                        <h3 className="font-serif text-xl uppercase tracking-widest text-theme-text mb-6 flex items-center gap-3">
                                            <div className="w-2 h-2 bg-gold rotate-45"></div>
                                            Intimidation Index Score
                                        </h3>
                                        <div className="bg-black/20 p-8 border border-solid border-gold/30 hover:border-gold/100 transition-colors duration-300 shadow-md backdrop-blur-sm group h-full">
                                            <div className="flex justify-between text-xs tracking-[0.2em] uppercase text-theme-text/80 font-bold mb-4">
                                                <span>Total Matrix Score</span>
                                                <span className="text-gold">{profile.total} / 25</span>
                                            </div>
                                            <div className="w-full h-1 bg-theme-border/30 relative overflow-hidden mb-6">
                                                <motion.div
                                                    initial={{ width: 0 }}
                                                    animate={{ width: `${percentage}%` }}
                                                    transition={{ duration: 1.5, ease: "easeOut" }}
                                                    className="absolute top-0 left-0 h-full bg-gold"
                                                />
                                            </div>
                                            
                                            <div className="mt-8">
                                            {profile.profileType === "extreme" && (
                                                <p className="text-theme-text/80 font-light text-sm leading-relaxed">
                                                    Your professional success has become an elegant, highly intellectualized fortress that keeps potential partners at a safe, manageable distance under the guise of them being "intimidated." By convincing yourself that your strength is the problem, you bypass the deeper, more uncomfortable truth: that absolute self-reliance is your primary defense against the terrifying risk of being vulnerable.
                                                </p>
                                            )}
                                            {profile.profileType === "emerging" && (
                                                <p className="text-theme-text/80 font-light text-sm leading-relaxed">
                                                    You are beginning to use your professional competence as an emotional buffer, occasionally retreating into your achievements when a relationship feels unpredictable or exposing. While your independence is genuine, it is subtly morphing into a defensive strategy designed to protect you from the discomfort of not being in control.
                                                </p>
                                            )}
                                            {profile.profileType === "secure" && (
                                                <p className="text-theme-text/80 font-light text-sm leading-relaxed">
                                                    You have successfully decoupled your professional achievements from your intrinsic worth, allowing you to approach intimacy without needing your success to act as an emotional shield. You recognize that true vulnerability is not a threat to your competence, but rather the very currency required to build a deeply connected, equal partnership.
                                                </p>
                                            )}
                                            </div>

                                        </div>
                                    </div>

                                    <div>
                                        <h3 className="font-serif text-xl uppercase tracking-widest text-theme-text mb-6 flex items-center gap-3">
                                            <div className="w-2 h-2 bg-gold rotate-45"></div>
                                            Systemic Pattern Analysis
                                        </h3>
                                        <div className="bg-black/20 p-8 border border-solid border-gold/30 hover:border-gold/100 transition-colors duration-300 shadow-md backdrop-blur-sm group h-full">
                                            {profile.profileType === "extreme" && (
                                                <>
                                                    <strong className="font-medium text-theme-text uppercase text-xs tracking-[0.2em] block mb-2 text-gold">Primary Diagnostic Focus</strong>
                                                    <span className="text-theme-text/80 font-light text-sm leading-relaxed block mb-4">
                                                        Real intimacy requires the courage to step out from behind your achievements and allow yourself to be met, not for what you can execute, but for who you are when the armor is removed.
                                                    </span>
                                                </>
                                            )}
                                            
                                            {profile.profileType === "emerging" && (
                                                <>
                                                    <strong className="font-medium text-theme-text uppercase text-xs tracking-[0.2em] block mb-2 text-gold">Primary Diagnostic Focus</strong>
                                                    <span className="text-theme-text/80 font-light text-sm leading-relaxed block mb-4">
                                                        Gently lowering this guard will allow you to experience intimacy as a safe harbor rather than another arena where you must perform.
                                                    </span>
                                                </>
                                            )}
                                            
                                            {profile.profileType === "secure" && (
                                                <>
                                                    <strong className="font-medium text-theme-text uppercase text-xs tracking-[0.2em] block mb-2 text-gold">Primary Diagnostic Focus</strong>
                                                    <span className="text-theme-text/80 font-light text-sm leading-relaxed block mb-4">
                                                        By remaining open to emotional risk, you invite genuine connection rather than defensive power dynamics. Continue relying on transparency over fortification.
                                                    </span>
                                                </>
                                            )}

                                            <strong className="font-medium text-theme-text uppercase text-xs tracking-[0.2em] block mb-2 text-gold mt-8 border-t border-theme-border/50 pt-8">Clinical Note</strong>
                                            <p className="text-theme-text/80 font-light text-sm leading-relaxed mt-2 italic">
                                                Self-Reliance is functional up to a point; beyond that, it is merely isolation dressed in high-status clothing.
                                            </p>
                                        </div>
                                    </div>
                                </motion.div>

                                <motion.div variants={itemVariants} className="w-full h-[1px] bg-gold/30 mb-20" />

                                <motion.div variants={itemVariants} className="bg-black/20 p-8 md:p-12 text-center relative overflow-hidden shadow-2xl border border-solid border-gold/30 hover:border-gold/100 transition-colors duration-300 backdrop-blur-sm group">
                                    <div className="absolute top-0 left-0 w-full h-1 bg-gold hover:opacity-100 transition-opacity"></div>
                                    <h3 className="font-serif text-xl md:text-2xl uppercase tracking-widest mb-6 text-theme-text">Strategic Rectification</h3>
                                    
                                    <p className="text-theme-text/70 font-light text-sm md:text-base mb-8 max-w-2xl mx-auto leading-relaxed border-b border-theme-border pb-8">
                                        Distinguishing the difference between high absolute standards and defensive self-protection patterns is critical for executives entering the dating market.
                                    </p>

                                    <div className="w-full flex flex-col items-center gap-6 mt-8">
                                        <a href={AFFILIATE_LINKS.ONLINE_THERAPY} target="_blank" rel="noopener noreferrer" className="btn-gold inline-block w-full sm:w-auto px-12">
                                            Explore Counseling Networks
                                        </a>
                                        <Link to="/vault" className="text-[10px] tracking-widest uppercase text-theme-text/60 hover:text-gold transition-colors block pb-2 border-b border-transparent hover:border-gold/30 mt-4">
                                            Return to the Vault
                                        </Link>
                                    </div>
                                </motion.div>
                            </motion.div>
                        );
                    })()}
                </AnimatePresence>
            </main>
        </div>
    );
}
